import React, {
  ReactElement,
  useContext,
  useEffect,
  useState,
  useMemo,
  createContext,
  useReducer
} from "react";
import i18next, { useTranslation } from "utilities/i18n";
import {
  ConfigurationAttribute,
  DeviceParameterFragment,
  ExtendedDeviceParameterFragment,
  GroupDto,
  GroupFragment,
  HardwareModel,
  Maybe,
  Param2DArrayItem,
  ParamArrayItem,
  PoliciesQueryVariables,
  PolicyDeploymentScope,
  PolicyFragment,
  PolicyType,
  Site,
  SoftwareSupportUpdatePath,
  SwUpdateSource,
  useDeviceDetailsQuery,
  useDeviceGroupsQuery,
  useDeviceParametersQuery,
  useGroupsForDeviceQuery,
  useHardwareModelsQuery,
  usePoliciesQuery
} from "generated/graphql";
import { NavLink, Route, Switch, useHistory } from "polyrouter";
import { LoadingIndicator, PolyIcon, useFeatureFlagWithTenant } from "elements";
import { DeviceSettings } from "./ProvisionedDeviceSettings";
import {
  DEVICES_WITH_ADDITIONAL_SW_UPDATE_PATHS,
  CALL_CONFIGURATION,
  CUSTOM_SUPPORT_UPDATE_PATH,
  CUSTOM_UPDATE_PATH,
  DEFAULT_CATEGORY,
  DEFAULT_PARAM_ARRAY_COUNT,
  DEFAULT_SUBCATEGORY,
  DEVICE_PARAM_TYPE,
  INHERITANCE_ORDER,
  INTEGRATIONS,
  LENS_USER_TYPE,
  LINE,
  LINES,
  LINE_LIMIT_PARAM,
  MAIN,
  NO_GROUPING,
  PARAM_ARRAY_PLACEHOLDER,
  PARAM_TYPE,
  SERVER,
  SERVER_SETTINGS_NAME,
  SOFTWARE_UPDATE,
  TEAMS,
  UPDATE_PATH,
  UPDATE_SOURCE,
  AUTO_UPDATE_LATEST
} from "Constants";
import "routes/Manage/Inventory/View/DeviceDetails.scss";
import { DetailProps } from "routes/Manage/Inventory/View/DeviceDetails";
import { useDispatch, useSelector } from "react-redux";
import { State } from "store";
import BlockingForm from "collections/Form/BlockingForm";
import { constructFilterList } from "utilities/filters/deviceFilters";
import queryString from "query-string";
import { getUnsavedChangesCount } from "./MySettings";
import { resetChanges } from "store/settings";
import { findCatalogModel } from "utilities/modelSelectors";
import { AllQueryParam } from "polyrouter/allQueryParams";
import { isProvisionedDevice } from "elements/ConnectionStatusIndicator/ConnectionStatusIndicator";
import { SelectedTenantContext } from "polyrouter/SelectedTenantProvider";
import { provisionedDeviceSettingInheritance } from "store/provisionedDeviceSettingInheritance";
import { SwSupportUpdatePathFragment } from "generated/graphql.alias";
import { createReducer, createStandardAction } from "typesafe-actions";

export type SiteInfo = Pick<Site, "id" | "name">;
export type GroupInfo = Pick<GroupDto, "id" | "name">;
export type SettingValue =
  | string
  | number
  | boolean
  | SwSupportUpdatePathFragment[];
export type SettingsValues = {
  [key: string]: SettingValue;
};

export type Setting = DeviceParameterFragment & {
  category: string;
  subcategory: string;
  settingName: string;
  value: SettingValue;
  index1?: number;
  index2?: number;
  policyDeploymentScope: PolicyDeploymentScope | null;
};

export type SettingsTree = {
  [category: string]: {
    [subcategory: string]: {
      [collection: string]: {
        [grouping: string]:
          | {
              [subGrouping: string]: Setting[];
            }
          | Setting[];
      };
    };
  };
};

type NumOfLines = {
  regLimit: number;
  maxConfiguredLine: number;
};

export type PolicyDetails = Omit<PolicyFragment, "__typename">;
export type InheritanceDetails = {
  [name: string]: {
    value: SettingValue;
    scope: PolicyDeploymentScope | null;
    id: string;
    groupPriorityIndex?: number;
  };
};

const isDevicePolicy = (scope: PolicyDeploymentScope) =>
  scope === PolicyDeploymentScope.Device;
const isSitePolicy = (scope: PolicyDeploymentScope) =>
  scope === PolicyDeploymentScope.Site;
const isGroupPolicy = (scope: PolicyDeploymentScope) =>
  scope === PolicyDeploymentScope.Group;

export const constructPolicyName = (
  model: string,
  type: PolicyType,
  scope: PolicyDeploymentScope,
  id?: string,
  siteId?: string,
  groupId?: string
) => {
  const idStr = isDevicePolicy(scope) ? `(${id})` : "";
  const siteStr = isSitePolicy(scope) ? ` ${i18next.t("SITE")}(${siteId})` : "";
  const groupStr = isGroupPolicy(scope)
    ? ` ${i18next.t("DEVICE_GROUP")}(${groupId})`
    : "";
  const typeStr =
    type === PolicyType.DeviceSettingsUpdate
      ? i18next.t("SETTINGS")
      : i18next.t("UPDATE");

  return `${model}${idStr}${siteStr}${groupStr} ${typeStr} ${i18next.t(
    "POLICY"
  )}`;
};

export type ProvisionedDevicePolicyScopeState = {
  policyScope: PolicyDeploymentScope;
};

export type SetProvisionedDevicePolicyScope = PolicyDeploymentScope;

const defaultProvisionedDevicePolicyScopeState = {
  policyScope: PolicyDeploymentScope.DeviceModel
};

export const setProvisionedDevicePolicyScope = createStandardAction(
  "SET_PROVISIONED_DEVICE_POLICY_SCOPE"
)<SetProvisionedDevicePolicyScope>();

export const ProvisionedDevicePolicyScopeReducer = createReducer(
  defaultProvisionedDevicePolicyScopeState
).handleAction(setProvisionedDevicePolicyScope, (state, action) => {
  return {
    ...state,
    policyScope: action.payload
  };
});

export const ProvisionedDevicePolicyScopeContext = createContext<
  [ProvisionedDevicePolicyScopeState, React.Dispatch<any>]
>([defaultProvisionedDevicePolicyScopeState, () => null]);

export const useProvisionedDevicePolicyScopeContext = () => {
  return useContext(ProvisionedDevicePolicyScopeContext);
};

export const ProvisionedDevicePolicyScopeProvider: React.FC = props => {
  const [state, dispatch] = useReducer(
    ProvisionedDevicePolicyScopeReducer,
    defaultProvisionedDevicePolicyScopeState
  );
  return (
    <ProvisionedDevicePolicyScopeContext.Provider
      value={[state, dispatch]}
      {...props}
    />
  );
};

export const computeParamArrayName = (name: string, replacements: number[]) =>
  replacements.reduce(
    (computed, replacement, index) =>
      computed.replace(
        new RegExp(PARAM_ARRAY_PLACEHOLDER + (index + 1), "i"),
        replacement.toString()
      ),
    name
  );

export const constructCustomSwSupportUpdatePath = (model: string) => {
  return DEVICES_WITH_ADDITIONAL_SW_UPDATE_PATHS.filter(({ models }) =>
    models.includes(model)
  ).reduce((swUpdatePaths, { additionalUpdatePathsToDisplay }) => {
    swUpdatePaths.push(
      ...additionalUpdatePathsToDisplay
        .filter(deviceModel => deviceModel !== model)
        .map(deviceModel => ({
          __typename: "SoftwareSupportUpdatePath" as const,
          deviceModel,
          swPath: ""
        }))
    );

    return swUpdatePaths;
  }, [] as SoftwareSupportUpdatePath[]);
};

export const isLines = (category: string | null, subcategory: string | null) =>
  category === CALL_CONFIGURATION && subcategory === LINES;

const getNumOfLines = (
  params: Maybe<ExtendedDeviceParameterFragment>[]
): NumOfLines => {
  // we need to go through all settings to find out what is maximum configured Line
  let maxConfiguredLine = 0;
  let regLimit = 0;
  params.forEach(param => {
    if (param && param.metadata) {
      if (param.name === LINE_LIMIT_PARAM) {
        regLimit = parseInt(
          param?.policyAttribute?.currentValue ||
            param?.metadata?.defaultValue ||
            "0"
        );
      } else {
        const { categories, paramType } = param.metadata;
        const [category, subcategory] = categories ?? [];
        if (isLines(category, subcategory)) {
          if (paramType === PARAM_TYPE.PARAM_ARRAY) {
            maxConfiguredLine =
              param.policyAttribute?.paramArray?.reduce((max, current) => {
                const lineNum = current?.replace1 ?? 0;
                return lineNum > max ? lineNum : max;
              }, maxConfiguredLine) ?? maxConfiguredLine;
          } else if (paramType === PARAM_TYPE.PARAM_2D_ARRAY) {
            maxConfiguredLine =
              param.policyAttribute?.param2DArray?.reduce((max, current) => {
                const lineNum = current?.replace1 ?? 0;
                return lineNum > max ? lineNum : max;
              }, maxConfiguredLine) ?? maxConfiguredLine;
          }
        }
      }
    }
  });

  return {
    regLimit,
    maxConfiguredLine
  };
};

const showLineWarning = (
  category: string,
  subcategory: string,
  collection: string,
  numOfLines: NumOfLines
) => {
  let showWarning = false;
  if (isLines(category, subcategory)) {
    const lineNumber = collection.match(/\d+/)?.map(Number)[0];
    if (lineNumber && lineNumber > numOfLines.regLimit) {
      showWarning = true;
    }
  }

  return showWarning;
};

const getParam = function<T extends ParamArrayItem | Param2DArrayItem>(
  name: string,
  paramArray: (T | null)[],
  predicate: (value: T) => boolean,
  replacements: number[],
  defaultValue: string | null | undefined,
  policyDeploymentScope: PolicyDeploymentScope | null
) {
  const value =
    paramArray.filter(item => item && predicate(item))[0]?.currentValue ?? "";
  return {
    name: computeParamArrayName(name, replacements),
    value,
    defaultValue,
    policyDeploymentScope
  };
};

const setValue = (
  values: SettingsValues,
  defaultValues: SettingsValues,
  name: string,
  policyAttribute: ConfigurationAttribute | null,
  settingMetadata: DeviceParameterFragment,
  policyDeploymentScope: PolicyDeploymentScope | null,
  currentScope: PolicyDeploymentScope
) => {
  const {
    type,
    paramType,
    defaultValue,
    numReplace1Max,
    paramArray,
    numReplace2Max,
    param2DArray
  } = settingMetadata;

  let params = [];
  if (
    paramType === PARAM_TYPE.PARAM_ARRAY ||
    paramType === PARAM_TYPE.PARAM_2D_ARRAY
  ) {
    const max1 = numReplace1Max || DEFAULT_PARAM_ARRAY_COUNT;
    const max2 = numReplace2Max || DEFAULT_PARAM_ARRAY_COUNT;
    for (let i = 0; i < max1; i++) {
      const index1 = i + 1;
      if (paramType === PARAM_TYPE.PARAM_2D_ARRAY) {
        for (let j = 0; j < max2; j++) {
          const index2 = j + 1;
          params.push(
            getParam<Param2DArrayItem>(
              name,
              policyAttribute?.param2DArray || [],
              param => param.replace1 === index1 && param.replace2 === index2,
              [index1, index2],
              param2DArray?.[i]?.[j],
              policyDeploymentScope
            )
          );
        }
      } else {
        params.push(
          getParam<ParamArrayItem>(
            name,
            policyAttribute?.paramArray || [],
            param => param.replace1 === index1,
            [index1],
            paramArray?.[i],
            policyDeploymentScope
          )
        );
      }
    }
  } else {
    params.push({
      name,
      value: policyAttribute?.currentValue,
      defaultValue,
      policyDeploymentScope
    });
  }

  params.forEach(param => {
    const defaultValue = param.defaultValue ?? "";
    let value: SettingValue = defaultValue;
    if (
      currentScope &&
      (param.policyDeploymentScope === currentScope ||
        currentScope === PolicyDeploymentScope.Device)
    ) {
      value = param.value ?? "";
    }
    switch (type) {
      case DEVICE_PARAM_TYPE.BOOL:
        value = (value || defaultValue) === "1";
        break;
      case DEVICE_PARAM_TYPE.XBOOL:
        value = (value || defaultValue) === "True";
        break;
      case DEVICE_PARAM_TYPE.UINT:
      case DEVICE_PARAM_TYPE.UINT_NULL:
      case DEVICE_PARAM_TYPE.SINT:
      case DEVICE_PARAM_TYPE.SINT_NULL:
        // in some cases value might not be a number, even if type suggests that (e.g. "")
        // in those cases leave value as is
        const parsedValue = parseInt(`${value}`);
        if (!isNaN(parsedValue)) {
          value = parsedValue;
        }
        break;
    }

    values[param.name] = value;
    defaultValues[param.name] = defaultValue;
  });
};

const putSetting = (
  settings: Setting[],
  setting: Setting,
  groupingOrder: number | null
) => {
  if (groupingOrder) {
    // grouping order starts from 1
    settings[groupingOrder - 1] = setting;
  } else {
    settings.push(setting);
  }
};

const addSetting = (
  settingsTree: SettingsTree,
  values: SettingsValues,
  category: string,
  subcategory: string,
  collection: string,
  grouping: string,
  name: string,
  metadata: DeviceParameterFragment,
  policyDeploymentScope: PolicyDeploymentScope | null,
  groupingOrder: number | null,
  paramArrayIndexes: number[] = [],
  subGrouping: string = ""
) => {
  const settingsBySubcategory = (settingsTree[category] =
    settingsTree[category] || {});
  const settingsByCollection = (settingsBySubcategory[subcategory] =
    settingsBySubcategory[subcategory] || {});
  const settingsByGroup = (settingsByCollection[collection] =
    settingsByCollection[collection] || {});

  let settings: Setting[];
  if (subGrouping) {
    let settingsBySubgroup = (settingsByGroup[grouping] =
      settingsByGroup[grouping] || {}) as { [subGrouping: string]: Setting[] };
    settings = settingsBySubgroup[subGrouping] =
      settingsBySubgroup[subGrouping] || [];
  } else {
    settings = (settingsByGroup[grouping] =
      settingsByGroup[grouping] || []) as Setting[];
  }

  let settingName = name;
  let lensConditional = metadata.lensConditional ?? "";
  if (paramArrayIndexes.length) {
    settingName = computeParamArrayName(name, paramArrayIndexes);
    lensConditional = computeParamArrayName(lensConditional, paramArrayIndexes);
  }

  const setting: Setting = {
    ...metadata,
    category,
    subcategory,
    settingName,
    value: values[settingName],
    lensConditional,
    index1: paramArrayIndexes[0],
    index2: paramArrayIndexes[1],
    policyDeploymentScope
  };

  putSetting(settings, setting, groupingOrder);
};

const extractScopeDetails = (
  policy: PolicyFragment,
  deviceName: string,
  groupData: GroupFragment[],
  siteName: string
) => {
  switch (policy.policyDeploymentScope) {
    case PolicyDeploymentScope.Device:
      return {
        scopeId: policy.deviceId,
        scopeValue: deviceName
      };
    case PolicyDeploymentScope.DeviceModel:
      return {
        scopeId: policy.deviceModelUuid || policy.deviceModel,
        scopeValue: policy.deviceModel
      };
    case PolicyDeploymentScope.Group:
      const groupName = groupData.find(group => group.id === policy.groupId)
        ?.name;
      return {
        scopeId: policy.groupId,
        scopeValue: groupName
      };
    case PolicyDeploymentScope.Site:
      return {
        scopeId: policy.siteId,
        scopeValue: siteName
      };
    default:
      return {
        scopeId: undefined,
        scopeValue: undefined
      };
  }
};

const DeviceSettingsLoader: React.FC<{
  id?: string;
  model: string;
  siteId?: string;
  groupId?: string;
  policyScope: PolicyDeploymentScope;
  children: (
    deviceParameters: Maybe<ExtendedDeviceParameterFragment>[],
    settings: SettingsTree,
    numOfLines: NumOfLines,
    values: SettingsValues,
    defaultValues: SettingsValues
  ) => ReactElement;
  loaderClass?: string;
}> = ({
  id,
  model,
  siteId,
  groupId,
  children,
  loaderClass,
  policyScope: scope
}) => {
  const { tenant } = useContext(SelectedTenantContext);
  const tenantId = tenant?.id ?? "";
  const [isTACIntegrationEnabled] = useFeatureFlagWithTenant("TAC_INTEGRATION");
  const deviceModel = getDeviceModel(model);
  const [
    { policyScope },
    dispatchContext
  ] = useProvisionedDevicePolicyScopeContext();
  const { data, fetching } = useDeviceParametersQuery({
    variables: {
      tenantId: tenantId,
      deviceId: id,
      deviceModel,
      siteId,
      groupId,
      sendOnlyChanged: false
    }
  });

  useEffect(() => {
    dispatchContext(setProvisionedDevicePolicyScope(scope));
  }, [dispatchContext, scope]);

  if (fetching) {
    return <LoadingIndicator className={loaderClass} />;
  } else {
    let settingsTree: SettingsTree = {};
    let values: SettingsValues = {};
    let defaultValues: SettingsValues = {};
    // used in specific case, for category "Call Configuration" and subcategory "Lines"
    let numOfLines: NumOfLines = {
      regLimit: -1,
      maxConfiguredLine: -1
    };

    if (data && data.getDeviceParametersExtended) {
      const extendedDeviceParameters = data.getDeviceParametersExtended;
      extendedDeviceParameters.forEach(extendedDeviceParameter => {
        if (extendedDeviceParameter?.metadata) {
          const {
            name,
            paramType,
            categories,
            lensUserType
          } = extendedDeviceParameter.metadata;

          let [category, subcategory] = categories ?? [];
          // If Teams Admin Center integration feature is disabled do not display related settings
          if (
            category === INTEGRATIONS &&
            subcategory === TEAMS &&
            !isTACIntegrationEnabled
          ) {
            return;
          }

          if (name) {
            const settingName = name.toLowerCase();
            setValue(
              values,
              defaultValues,
              settingName,
              extendedDeviceParameter.policyAttribute,
              extendedDeviceParameter.metadata,
              extendedDeviceParameter.policyDeploymentScope,
              policyScope
            );

            // display parameters which are lensUserType=1 (SIMPLE_USER) or set in policy
            // and default parameters (policyAttribute exists but policyDeploymentScope is null/undefined) should be visible

            if (
              lensUserType === LENS_USER_TYPE.SIMPLE_USER ||
              extendedDeviceParameter.policyAttribute !== null
            ) {
              let {
                grouping,
                groupingOrder
              } = extendedDeviceParameter.metadata;
              category = category || DEFAULT_CATEGORY;
              subcategory = subcategory || DEFAULT_SUBCATEGORY;
              grouping = grouping || NO_GROUPING;

              // categories/subcategories might contain "/"
              // that can mess with routing so replace "/" with "|"
              category = category.replace(/\//g, "|");
              subcategory = subcategory.replace(/\//g, "|");

              // need special handling for these, at least for now
              const isLinesParam = isLines(category, subcategory);

              if (
                paramType === PARAM_TYPE.PARAM_ARRAY ||
                paramType === PARAM_TYPE.PARAM_2D_ARRAY
              ) {
                const collectionNameBase = isLinesParam ? LINE : subcategory;
                if (isLinesParam && numOfLines.regLimit === -1) {
                  numOfLines = getNumOfLines(extendedDeviceParameters);
                }
                const max1 =
                  (isLinesParam
                    ? Math.max(
                        numOfLines.maxConfiguredLine,
                        numOfLines.regLimit
                      )
                    : null) ||
                  extendedDeviceParameter.metadata.numReplace1Max ||
                  DEFAULT_PARAM_ARRAY_COUNT;

                for (let i = 0; i < max1; i++) {
                  const index1 = i + 1;
                  const collectionName = `${collectionNameBase} ${index1}`;

                  if (paramType === PARAM_TYPE.PARAM_ARRAY) {
                    const paramArray =
                      extendedDeviceParameter.policyAttribute?.paramArray;
                    const policyDeploymentScope = paramArray?.some(
                      param => param?.replace1 === index1
                    )
                      ? extendedDeviceParameter.policyDeploymentScope
                      : null;
                    addSetting(
                      settingsTree,
                      values,
                      category,
                      subcategory,
                      collectionName,
                      grouping,
                      settingName,
                      extendedDeviceParameter.metadata,
                      policyDeploymentScope,
                      groupingOrder,
                      [index1]
                    );
                  } else {
                    let groupingNameBase = grouping;
                    let subGrouping = "";
                    if (isLinesParam && name.includes(SERVER_SETTINGS_NAME)) {
                      groupingNameBase = SERVER;
                      subGrouping = grouping;
                    }

                    const max2 =
                      extendedDeviceParameter.metadata.numReplace2Max ||
                      DEFAULT_PARAM_ARRAY_COUNT;

                    for (let j = 0; j < max2; j++) {
                      const index2 = j + 1;
                      const groupingName = `${groupingNameBase} ${index2}`;

                      const param2DArray =
                        extendedDeviceParameter.policyAttribute?.param2DArray;
                      const policyDeploymentScope = param2DArray?.some(
                        param =>
                          param?.replace1 === index1 &&
                          param?.replace2 === index2
                      )
                        ? extendedDeviceParameter.policyDeploymentScope
                        : null;
                      addSetting(
                        settingsTree,
                        values,
                        category,
                        subcategory,
                        collectionName,
                        groupingName,
                        settingName,
                        extendedDeviceParameter.metadata,
                        policyDeploymentScope,
                        groupingOrder,
                        [index1, index2],
                        subGrouping
                      );
                    }
                  }
                }
              } else {
                addSetting(
                  settingsTree,
                  values,
                  category,
                  subcategory,
                  MAIN,
                  grouping,
                  settingName,
                  extendedDeviceParameter.metadata,
                  extendedDeviceParameter.policyDeploymentScope,
                  groupingOrder
                );
              }
            }
          }
        }
      });
    }

    // We need to do some additional processing
    // For PARAM_ARRAY settings we should check if same grouping is present in MAIN collection
    // If it is, then move setting there. After moving delete grouping/collection if there are no more settings present
    Object.values(settingsTree).forEach(settingsBySubcategory => {
      Object.values(settingsBySubcategory).forEach(settingsByCollection => {
        Object.entries(settingsByCollection).forEach(
          ([collection, settingsByGroup]) => {
            if (collection !== MAIN) {
              Object.entries(settingsByGroup).forEach(
                ([grouping, settings]) => {
                  if (Array.isArray(settings)) {
                    // check if corresponding grouping is present in MAIN collection of settings
                    const mainCollectionSettings =
                      settingsByCollection[MAIN]?.[grouping];
                    if (
                      mainCollectionSettings &&
                      Array.isArray(mainCollectionSettings)
                    ) {
                      // check if all settings in current grouping are of type PARAM_ARRAY (no PARAM_2D_ARRAY present)
                      const nonNullSettings = settings.filter(
                        setting => setting !== null
                      );
                      const isAllParamArray = nonNullSettings.every(
                        setting => setting.paramType === PARAM_TYPE.PARAM_ARRAY
                      );
                      if (isAllParamArray) {
                        nonNullSettings.forEach(setting => {
                          const { groupingOrder, index1 } = setting;
                          let order = 0;
                          if (groupingOrder && index1) {
                            order = groupingOrder + index1 - 1;
                          }
                          if (setting.friendlyName) {
                            setting.friendlyName += " " + index1;
                          }

                          putSetting(mainCollectionSettings, setting, order);
                        });

                        delete settingsByGroup[grouping];
                      }
                    }
                  }
                }
              );
              if (Object.keys(settingsByGroup).length === 0) {
                delete settingsByCollection[collection];
              }
            }
          }
        );
      });
    });

    return children(
      data?.getDeviceParametersExtended ?? [],
      settingsTree,
      numOfLines,
      values,
      defaultValues
    );
  }
};

export const PolicyLoader: React.FC<{
  id?: string;
  model: string;
  siteId?: string;
  type: PolicyType;
  calculateInheritance?: boolean;
  groupId?: string;
  deviceSerial?: Maybe<string>;
  isProvisioned?: boolean;
  children: (
    policy: PolicyDetails,
    inheritance?: InheritanceDetails
  ) => ReactElement;
}> = ({
  id,
  model,
  siteId,
  groupId,
  type,
  calculateInheritance,
  children,
  deviceSerial,
  isProvisioned
}) => {
  const { tenant } = useContext(SelectedTenantContext);
  const tenantId = tenant?.id ?? "";
  const deviceModel = getDeviceModel(model);
  const dispatch = useDispatch();
  const [{ policyScope }] = useProvisionedDevicePolicyScopeContext();
  // deviceId and siteId are not added since we want to fetch all policies(device, site and model) to calculate inheritance
  // deviceId and siteId are used later bellow to filter out only relevant policies
  const variables: PoliciesQueryVariables = {
    tenantId: tenantId,
    deviceModel
  };

  const { data: policiesData, fetching: policiesFetching } = usePoliciesQuery({
    variables
  });

  // if device is provisioned, we need to find out which groups it belongs to
  // so we can calculate inheritance appropriately
  const {
    data: groupsData,
    fetching: groupsFetching
  } = useGroupsForDeviceQuery({
    variables: {
      tenantId: tenantId,
      serial: deviceSerial ?? ""
    },
    pause: !isProvisioned || !deviceSerial
  });

  const {
    data: allGroupData,
    fetching: allGroupDataFetching
  } = useDeviceGroupsQuery({
    variables: {
      tenantId: tenantId
    }
  });

  // we need to find out site which this device belongs to
  // so we can include appropriate site level policies in inheritance calculations
  const {
    data: devicesData,
    fetching: devicesFetching
  } = useDeviceDetailsQuery({
    variables: {
      filter: {
        AND: constructFilterList({ id })
      }
    }
  });

  const {
    data: catalogModelsData,
    fetching: fetchingCatalogModels
  } = useHardwareModelsQuery();
  const fetching =
    policiesFetching ||
    devicesFetching ||
    fetchingCatalogModels ||
    allGroupDataFetching ||
    groupsFetching;

  const relevantGroupIds: (Maybe<string> | undefined)[] = useMemo(() => {
    let relevantGroupIds: (Maybe<string> | undefined)[] = [];
    // If looking at an individual device, relevantGroupIds is an array of groupIds to which the device belongs
    // If looking at a group policy, the only relevant groupId is the one in view
    // If looking at a site policy, no groups are relevant
    if (deviceSerial && groupsData?.getDevice?.groups?.length) {
      relevantGroupIds = groupsData.getDevice.groups;
    } else if (groupId) {
      relevantGroupIds = [groupId];
    }
    return relevantGroupIds;
  }, [groupsData, deviceSerial, groupId]);

  let relevantSiteId = siteId;
  let relevantSiteName: string = "";
  let deviceName: string = "";
  if (id && devicesData) {
    relevantSiteId = devicesData.devices[0]?.site?.id;
    relevantSiteName = devicesData.devices[0]?.site?.name || "";
    deviceName = devicesData.devices[0]?.name || "";
  }
  const relevantPolicies = useMemo(() => {
    return (
      policiesData?.getPolicies?.filter(policy => {
        const { policyDeploymentScope, policyType, deviceId, siteId, groupId } =
          policy ?? {};

        return (
          policyType === type &&
          policyDeploymentScope &&
          (policyDeploymentScope !== PolicyDeploymentScope.Device ||
            deviceId === id) &&
          (policyDeploymentScope !== PolicyDeploymentScope.Site ||
            siteId === relevantSiteId) &&
          (policyDeploymentScope !== PolicyDeploymentScope.Group ||
            relevantGroupIds.includes(groupId)) &&
          INHERITANCE_ORDER[policyScope] >=
            INHERITANCE_ORDER[policyDeploymentScope]
        );
      }) || []
    );
  }, [policiesData, id, relevantGroupIds, relevantSiteId, policyScope, type]);

  const inheritance: InheritanceDetails = useMemo(() => {
    let inheritance: InheritanceDetails = {};
    const allGroups = allGroupData?.getAllGroups?.groups?.groups || [];
    if (calculateInheritance) {
      relevantPolicies.forEach(policy => {
        if (policy?.policyDeploymentScope) {
          const level = INHERITANCE_ORDER[policy.policyDeploymentScope] ?? 0;
          let props: {
            name: string; //name of setting
            value: SettingValue; // scope value, i.e siteName, groupName
            id: string; // scope id, i.e. siteId
            scope: PolicyDeploymentScope;
          }[] = [];
          const { scopeValue, scopeId } = extractScopeDetails(
            policy,
            deviceName,
            allGroups,
            relevantSiteName
          );
          if (
            type === PolicyType.DeviceSwUpdate &&
            (policy.policyDeploymentScope === policyScope ||
              policyScope === PolicyDeploymentScope.Device)
          ) {
            props = [
              {
                name: UPDATE_SOURCE,
                value: policy[UPDATE_SOURCE]?.toString() || "",
                id: scopeId || "",
                scope: policy.policyDeploymentScope
              },
              {
                name: UPDATE_PATH,
                value: policy[UPDATE_PATH] || "",
                id: scopeId || "",
                scope: policy.policyDeploymentScope
              },
              {
                name: CUSTOM_UPDATE_PATH,
                value: policy[CUSTOM_UPDATE_PATH] || "",
                id: scopeId || "",
                scope: policy.policyDeploymentScope
              },
              {
                name: CUSTOM_SUPPORT_UPDATE_PATH,
                value:
                  policy[CUSTOM_SUPPORT_UPDATE_PATH]?.filter(
                    (swUpdate): swUpdate is SoftwareSupportUpdatePath =>
                      !!swUpdate
                  ) || [],
                id: scopeId || "",
                scope: policy.policyDeploymentScope
              }
            ];
          } else {
            props = (policy.configurationAttributes || []).map(attribute => {
              return {
                name: attribute?.name || "",
                value: scopeValue || "",
                id: scopeId || "",
                scope: policy.policyDeploymentScope!
              };
            });
          }

          props.forEach(prop => {
            const scope = inheritance[prop.name]?.scope;
            const currentLevel = scope ? INHERITANCE_ORDER[scope] ?? 0 : 0;
            const existingGroupPriorityIndex =
              inheritance[prop.name]?.groupPriorityIndex;
            const groupPriorityIndex = relevantGroupIds.indexOf(policy.groupId);
            if (currentLevel < level) {
              inheritance[prop.name] = {
                value: prop.value,
                scope: prop.scope,
                id: prop.id
              };
            }
            // if it's a group policy, add in priority index
            if (
              level === INHERITANCE_ORDER.GROUP &&
              currentLevel <= level &&
              (existingGroupPriorityIndex === undefined ||
                (existingGroupPriorityIndex &&
                  groupPriorityIndex < existingGroupPriorityIndex))
            ) {
              inheritance[prop.name] = {
                value: prop.value,
                scope: prop.scope,
                groupPriorityIndex: groupPriorityIndex,
                id: prop.id
              };
            }
          });
        }
      });
    }
    return inheritance;
  }, [
    allGroupData,
    calculateInheritance,
    deviceName,
    relevantGroupIds,
    relevantPolicies,
    relevantSiteName,
    type,
    policyScope
  ]);

  const updateInheritanceForDisplay: InheritanceDetails = useMemo(() => {
    let updateInheritanceForDisplay: InheritanceDetails = {};
    const allGroups = allGroupData?.getAllGroups?.groups?.groups || [];

    relevantPolicies.forEach(policy => {
      if (policy?.policyDeploymentScope && type === PolicyType.DeviceSwUpdate) {
        let updateProps: {
          name: string; //name of setting
          value: SettingValue; // scope value, i.e siteName, groupName
          id: string; // scope id, i.e. siteId
          scope: PolicyDeploymentScope;
        }[] = [];
        const { scopeValue, scopeId } = extractScopeDetails(
          policy,
          deviceName,
          allGroups,
          relevantSiteName
        );

        // This is for the inheritance display
        // The values are the "scope values", i.e. "Santa Cruz Site"
        updateProps = [
          {
            name: AUTO_UPDATE_LATEST,
            value: scopeValue || "",
            id: scopeId || "",
            scope: policy.policyDeploymentScope
          },
          {
            name: UPDATE_SOURCE,
            value: scopeValue || "",
            id: scopeId || "",
            scope: policy.policyDeploymentScope
          },
          {
            name: UPDATE_PATH,
            value: scopeValue || "",
            id: scopeId || "",
            scope: policy.policyDeploymentScope
          },
          {
            name: CUSTOM_UPDATE_PATH,
            value: scopeValue || "",
            id: scopeId || "",
            scope: policy.policyDeploymentScope
          },
          {
            name: CUSTOM_SUPPORT_UPDATE_PATH,
            value: scopeValue || "",
            id: scopeId || "",
            scope: policy.policyDeploymentScope
          }
        ];

        updateProps.forEach(prop => {
          updateInheritanceForDisplay[prop.name] = {
            value: prop.value,
            scope: prop.scope,
            id: prop.id
          };
        });
      }
    });

    return updateInheritanceForDisplay;
  }, [allGroupData, relevantPolicies, deviceName, relevantSiteName, type]);

  const catalogModels =
    (catalogModelsData?.hardwareModels as HardwareModel[]) ?? [];
  const catalogModel = findCatalogModel(model, catalogModels);

  const inheritanceCopy = Object.assign({}, inheritance);
  const inheritanceToDisplay = Object.assign(
    inheritanceCopy,
    updateInheritanceForDisplay
  );

  useEffect(() => {
    // set the scope's hardwareModel so we can access at any level
    dispatch(
      provisionedDeviceSettingInheritance({
        key: "hardwareModel",
        id: catalogModel?.id || "",
        value: catalogModel?.name || "",
        scope: PolicyDeploymentScope.Device
      })
    );

    Object.keys(inheritanceToDisplay).forEach(name => {
      if (inheritanceToDisplay[name].value) {
        dispatch(
          provisionedDeviceSettingInheritance({
            key: name,
            id: inheritanceToDisplay[name].id,
            value: inheritanceToDisplay[name].value as string,
            scope: inheritanceToDisplay[name].scope!
          })
        );
      }
    });
  }, [dispatch, inheritanceToDisplay, catalogModel]);

  const policy: PolicyDetails = useMemo(() => {
    return (
      relevantPolicies
        .filter(policy => policy?.policyDeploymentScope === policyScope)
        .map(policy => {
          //@ts-ignore
          delete policy?.__typename;
          return policy;
        })[0] || {
        policyId: null,
        name: constructPolicyName(
          deviceModel,
          type,
          policyScope,
          id,
          siteId,
          groupId
        ),
        tenantId: tenantId ?? "",
        deviceId: isDevicePolicy(policyScope) ? id! : null,
        siteId: isSitePolicy(policyScope) ? siteId! : null,
        groupId: isGroupPolicy(policyScope) ? groupId! : null,
        deviceModel,
        deviceModelUuid: null,
        deviceType: deviceModel,
        policyType: type,
        policyDeploymentScope: policyScope,
        configurationAttributes: null,
        swUpdatePath: "",
        customSwUpdatePath: "",
        customSwSupportUpdatePath:
          type === PolicyType.DeviceSwUpdate
            ? constructCustomSwSupportUpdatePath(deviceModel)
            : null,
        swUpdateSource: SwUpdateSource.StandardUrl
      }
    );
  }, [
    deviceModel,
    groupId,
    id,
    policyScope,
    relevantPolicies,
    siteId,
    tenantId,
    type
  ]);
  // set deviceModelUuid for new policies and for those stored without a value
  if (!policy.deviceModelUuid) {
    policy.deviceModelUuid = isDevicePolicy(policyScope)
      ? id!
      : catalogModel?.id ?? null;
  }
  if (fetching) {
    return <LoadingIndicator />;
  }
  return children(policy, inheritance);
};

const modelMap: Record<string, string> = {
  G7500: "G 7500",
  StudioX50: "Studio X50",
  StudioX30: "Studio X30",
  StudioX70: "Studio X70"
};
export const getDeviceModel = (model: string) => {
  if (!model) return model;

  // strip leading "Poly " from model
  const modelWithoutPoly = model.replace(/^(Poly )/i, "");
  return modelMap[modelWithoutPoly] || modelWithoutPoly;
};

export const SettingChangeIndicator: React.FC = () => (
  <span data-testid="setting-indicator" className="setting-indicator" />
);

export const ProvisionedSettingsNav: React.FC<{
  id?: string;
  model: string;
  siteId?: string;
  groupId?: string;
}> = ({ id, model, siteId, groupId }) => {
  const history = useHistory();
  const deviceModel = getDeviceModel(model);
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [filter, setFilter] = useState("");
  const settings = useSelector((s: State) => s.settings);
  const [{ policyScope }] = useProvisionedDevicePolicyScopeContext();
  useEffect(() => {
    // cleanup - reset settings changes when component is unmounted,
    // since we want to keep changes only for current settings session
    return () => {
      dispatch(resetChanges());
    };
  }, [dispatch]);

  const isSettingChanged = (
    category: string,
    subcategory: string,
    collection: string,
    setting: string
  ): boolean => !!settings[category]?.[subcategory]?.[collection]?.[setting];

  const isCollectionChanged = (
    category: string,
    subcategory: string,
    collection: string
  ) => !!settings[category]?.[subcategory]?.[collection];

  const isSubcategoryChanged = (category: string, subcategory: string) =>
    !!settings[category]?.[subcategory];

  const isCategoryChanged = (category: string) => !!settings[category];

  const changedSettingsCount: number = getUnsavedChangesCount(settings);
  const persistParams: AllQueryParam[] = [
    "PolicyFilterState",
    "deviceModel",
    "device",
    "site",
    "DeviceFilterState",
    "InsightsFilterState",
    "group",
    "previous"
  ];
  return (
    <ul className="menu router sub scroll">
      <BlockingForm
        unsavedChanges={Object.keys(settings).length > 0}
        allowedSubRoutes={[
          "/settings",
          "/my-settings",
          "/manage/policy/details"
        ]}
      >
        <DeviceSettingsLoader
          id={id}
          model={deviceModel}
          siteId={siteId}
          groupId={groupId}
          policyScope={policyScope}
          loaderClass="text-left inline sm"
        >
          {(_, settings, numOfLines) => (
            <>
              <li className="filter mb-1 mt-1">
                <PolyIcon icon="filter" className="v-middle" />
                <input
                  type={"text"}
                  id={"filter-input"}
                  className="form-control floating-input"
                  placeholder={t("FILTER_SETTINGS")}
                  value={filter ?? ""}
                  onChange={e => setFilter(e.target.value)}
                />
              </li>
              <li>
                <NavLink
                  nest
                  to={{
                    pathname: "/my-settings",
                    search: history.location.search
                  }}
                >
                  {changedSettingsCount > 0 && <SettingChangeIndicator />}
                  {t("MY_SETTINGS")}
                </NavLink>
              </li>
              <li className="divider mt-5" />
              {Object.keys(settings).map(category => (
                <li key={category + "-key"} className="active-transparent">
                  <NavLink nest to={`/${category}`} persist={persistParams}>
                    {isCategoryChanged(category) && <SettingChangeIndicator />}
                    {category}
                  </NavLink>
                  {filter ? (
                    <ul className="menu router sub pl-1">
                      {Object.keys(settings[category]).map(subcategory =>
                        Object.keys(settings[category][subcategory]).map(
                          collection =>
                            Object.values(
                              settings[category][subcategory][collection]
                            ).map(groupSettings => {
                              const settingsToFilter = Array.isArray(
                                groupSettings
                              )
                                ? groupSettings
                                : Object.values(groupSettings).flat();

                              return settingsToFilter
                                .filter((setting: Setting) => {
                                  const settingName = setting.settingName.toLowerCase();
                                  const friendlyName = setting.friendlyName?.toLowerCase();
                                  const searchTerm = filter.toLowerCase();
                                  return (
                                    friendlyName?.includes(searchTerm) ||
                                    settingName.includes(searchTerm)
                                  );
                                })
                                .map((setting: Setting) => {
                                  const name = setting.settingName;
                                  return (
                                    <li key={name + "-key"}>
                                      <NavLink
                                        nest
                                        to={{
                                          pathname: `/${category}/${subcategory}/${collection}`,
                                          search: `setting=${name}`
                                        }}
                                        persist={persistParams}
                                        isActive={(_, location) => {
                                          const { setting } = queryString.parse(
                                            location.search
                                          );
                                          return !!name && setting === name;
                                        }}
                                      >
                                        {isSettingChanged(
                                          category,
                                          subcategory,
                                          collection,
                                          name
                                        ) && <SettingChangeIndicator />}
                                        {setting.friendlyName || name}
                                      </NavLink>
                                    </li>
                                  );
                                });
                            })
                        )
                      )}
                    </ul>
                  ) : (
                    <Route nest path={`/${category}`}>
                      <ul className="menu router sub pl-1">
                        {Object.keys(settings[category]).map(subcategory => (
                          <li key={subcategory + "-key"}>
                            <NavLink
                              nest
                              to={`/${subcategory}`}
                              persist={persistParams}
                            >
                              {isSubcategoryChanged(category, subcategory) && (
                                <SettingChangeIndicator />
                              )}
                              {subcategory}
                            </NavLink>
                            <Route nest path={`/${subcategory}`}>
                              <ul className="menu router sub pl-1">
                                {Object.keys(
                                  settings[category][subcategory]
                                ).map(collection => {
                                  if (collection === MAIN) return null;
                                  const showWarning = showLineWarning(
                                    category,
                                    subcategory,
                                    collection,
                                    numOfLines
                                  );

                                  return (
                                    <li key={collection + "-key"}>
                                      <NavLink
                                        nest
                                        to={{
                                          pathname: `/${collection}`,
                                          search: `warning=${showWarning}`
                                        }}
                                        persist={persistParams}
                                      >
                                        {isCollectionChanged(
                                          category,
                                          subcategory,
                                          collection
                                        ) && <SettingChangeIndicator />}
                                        {collection}
                                        {showWarning && (
                                          <PolyIcon
                                            className="float-right"
                                            icon="eye_slash"
                                          />
                                        )}
                                      </NavLink>
                                    </li>
                                  );
                                })}
                              </ul>
                            </Route>
                          </li>
                        ))}
                      </ul>
                    </Route>
                  )}
                </li>
              ))}
            </>
          )}
        </DeviceSettingsLoader>
        <li>
          <NavLink nest exact to="/update" persist={persistParams}>
            {isCategoryChanged(SOFTWARE_UPDATE) && <SettingChangeIndicator />}
            {t("SOFTWARE_UPDATE")}
          </NavLink>
        </li>
      </BlockingForm>
    </ul>
  );
};

export const ProvisionedDeviceSettingsRouter: React.FC<{
  device?: DetailProps | any;
  id?: string;
  model: string;
  site?: SiteInfo;
  group?: GroupInfo;
  isPolicyView?: boolean;
}> = ({ device, id, model, site, group, isPolicyView = false }) => {
  const scope = id
    ? PolicyDeploymentScope.Device
    : site?.id
    ? PolicyDeploymentScope.Site
    : group?.id
    ? PolicyDeploymentScope.Group
    : PolicyDeploymentScope.DeviceModel;

  return (
    <ProvisionedDevicePolicyScopeProvider>
      <Switch>
        <DeviceSettingsLoader
          id={id}
          model={model}
          siteId={site?.id}
          groupId={group?.id}
          policyScope={scope}
        >
          {(deviceParameters, settings, numOfLines, values, defaultValues) => (
            <Route nest path="/">
              <PolicyLoader
                id={id}
                model={model}
                siteId={site?.id}
                groupId={group?.id}
                type={PolicyType.DeviceSettingsUpdate}
                calculateInheritance={true}
                deviceSerial={device?.serialNumber || id}
                isProvisioned={isProvisionedDevice(
                  device?.hardwareModel,
                  device?.lastConfigRequestDate
                )}
              >
                {settingsPolicy => (
                  <PolicyLoader
                    id={id}
                    model={model}
                    siteId={site?.id}
                    groupId={group?.id}
                    deviceSerial={device?.serialNumber}
                    type={PolicyType.DeviceSwUpdate}
                    calculateInheritance
                  >
                    {(updatePolicy, updateInheritance) => (
                      <DeviceSettings
                        device={
                          scope === PolicyDeploymentScope.Device ? device : null
                        }
                        siteName={site?.name}
                        groupName={group?.name}
                        deviceParameters={deviceParameters}
                        settings={settings}
                        values={values ?? {}}
                        defaultValues={defaultValues ?? {}}
                        settingsPolicy={settingsPolicy}
                        swUpdatePolicy={updatePolicy}
                        swUpdateInheritance={updateInheritance}
                        isPolicyView={isPolicyView}
                        maxConfiguredLine={numOfLines.maxConfiguredLine}
                      />
                    )}
                  </PolicyLoader>
                )}
              </PolicyLoader>
            </Route>
          )}
        </DeviceSettingsLoader>
      </Switch>
    </ProvisionedDevicePolicyScopeProvider>
  );
};

export default DeviceSettingsLoader;
